<template>
  <generic-modal :title="$t('order')" ref="generic_modal">
    <div class="box mb-4">
      <div
        class="is-flex is-justify-content-space-between is-align-items-center mb-2"
      >
        <div class="title is-4 mb-0">
          {{ meta.name }}
        </div>
        <div class="has-text-grey is-size-7">
          <i class="fa fa-calendar mr-1"></i>
          {{ format_date(meta.create_date) }}
        </div>
      </div>
      <div class="is-flex is-align-items-center mb-2">
        <span class="tag is-info mr-2">{{ meta.grade }}</span>
        <span class="icon is-small mr-1"><i class="fa fa-phone"></i></span>
        <span class="mr-3">{{ meta.phone }}</span>
        <span class="icon is-small mr-1"><i class="fa fa-envelope"></i></span>
        <span>{{ meta.email }}</span>
      </div>
      <div
        class="notification is-light is-info py-2 px-3 mb-2"
        v-if="
          meta.settings &&
          meta.settings.billing_name &&
          meta.settings.billing_nit
        "
        style="font-size: 0.97em"
      >
        <span class="has-text-weight-semibold"
          >{{ $t("billing information") }}:</span
        >
        {{ $t("name") }} <strong>{{ meta.settings.billing_name }}</strong
        >, {{ $t("nit") }} <strong>{{ meta.settings.billing_nit }}</strong>
      </div>
    </div>

    <div class="box mb-4">
      <div class="title is-5 mb-2">{{ $t("order") }}</div>
      <table class="table is-fullwidth is-narrow mb-0">
        <thead>
          <tr>
            <th>{{ $t("quantity") }}</th>
            <th>{{ $t("product") }}</th>
            <th>{{ $t("size") }}</th>
            <th>{{ $t("customization") }}</th>
            <th class="has-text-right">{{ $t("total") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(product, pidx) in meta.tailoring_order_items" :key="pidx">
            <td>{{ Number(product.quantity) }}</td>
            <td>{{ product.product.name }}</td>
            <td>{{ product.size.toUpperCase() }}</td>
            <td>
              <span v-if="product.customization">{{
                product.customization
              }}</span>
              <span v-else>-</span>
            </td>
            <td class="has-text-right">
              {{
                format_currency(
                  Number(product.quantity) * Number(product.price)
                )
              }}
            </td>
          </tr>
        </tbody>
      </table>
      <div class="is-flex is-justify-content-flex-end mt-2">
        <span class="title is-5 has-text-success">
          {{ $t("total") }} {{ format_currency(meta.total) }}
        </span>
      </div>
    </div>

    <div class="box mb-4">
      <div class="title is-5 mb-2">{{ $t("payments") }}</div>
      <table class="table is-fullwidth is-narrow mb-0">
        <thead>
          <tr>
            <th>{{ $t("date") }}</th>
            <th>{{ $t("type") }}</th>
            <th>{{ $t("details") }}</th>
            <th class="has-text-right">{{ $t("amount") }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(payment, pidx) in meta.tailoring_order_payments"
            :key="pidx"
          >
            <td>{{ format_date(payment.date) }}</td>
            <td>
              <span class="tag is-warning is-light">{{
                $t(payment.type)
              }}</span>
              <span class="tag is-info is-light ml-1" v-if="payment.bank">{{
                payment.bank
              }}</span>
            </td>
            <td>
              <span
                class="tag is-primary is-light mr-1"
                v-if="payment.document_number"
              >
                {{ payment.document_number }}
              </span>
              <span
                class="tag is-primary is-light mr-1"
                v-if="payment.receipt_number"
              >
                {{ payment.receipt_number }}
              </span>
              <span
                class="tag is-link is-light mr-1"
                v-if="payment.document_file"
                @click="download_payment_document(payment)"
                style="cursor: pointer"
              >
                <span class="icon"><i class="fa fa-download"></i></span>
                <span>{{ $t("download") }}</span>
              </span>
              <span
                class="tag is-success is-light"
                v-if="payment.document_file"
                @click="
                  show_receipt = !show_receipt;
                  target_filename = payment.document_file.settings.final_name;
                "
                style="cursor: pointer"
              >
                <span class="icon"><i class="fa fa-eye"></i></span>
                <span>{{ $t("view") }}</span>
              </span>
            </td>
            <td class="has-text-right">
              {{ format_currency(payment.amount) }}
            </td>
            <td>
              <span
                v-if="
                  show_receipt &&
                  target_filename === payment.document_file.settings.final_name
                "
              >
                <img
                  :src="`${api_host}/drive/static/${target_filename}`"
                  alt="payment receipt"
                  style="
                    max-width: 120px;
                    border-radius: 8px;
                    border: 1px solid #e1e8ed;
                  "
                />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="is-flex is-justify-content-space-between mt-2">
        <span class="title is-6 has-text-info">
          {{ $t("total paid") }} {{ format_currency(meta.total_paid) }}
        </span>
        <span class="title is-6 has-text-warning">
          {{ $t("pending") }}
          {{ format_currency(meta.total - meta.total_paid) }}
        </span>
      </div>
    </div>

    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>
    <template v-slot:footer>
      <button class="button is-rounded" @click="close_modal">
        <span class="icon">
          <i class="fa fa-close"></i>
        </span>
        <span>{{ $t("cancel") }}</span>
      </button>
    </template>
  </generic-modal>
</template>
<script>
import {
  put,
  post,
  generate_error_message_text,
  format_date,
  download,
  api_host,
  format_currency,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";

export default {
  name: "OrderModal",
  components: { GenericModal },
  props: {},
  data() {
    return {
      meta: {},
      error: "",
      show_receipt: false,
      target_filename: "",
      api_host,
    };
  },
  computed: {
    is_form_invalid() {
      let v = this;
      return false;
      // return !v.meta.name || !v.meta.username || !v.meta.password;
    },
  },
  methods: {
    format_currency,
    format_date,
    close_modal() {
      let v = this;
      v.set_initial_state();
      v.$refs.generic_modal.close_modal();
    },
    show_modal(profile) {
      let v = this;
      v.set_initial_state();
      v.meta = profile;
      v.$refs.generic_modal.show_modal();
    },
    save() {
      let v = this;
      let profile = { ...v.meta };
      v.$emit("success", profile);
      v.$refs.generic_modal.close_modal();
    },
    set_initial_state() {
      let v = this;
      v.provider_uuid = undefined;
      v.meta = {
        name: "",
      };
      v.error = "";
      v.show_receipt = false;
      v.target_filename = "";
    },
    download_payment_document(payment) {
      console.log(payment);
      // let v = this;

      let final_name_has_extension =
        payment.document_file.settings.final_name.includes(".");
      if (final_name_has_extension) {
        let url = `${api_host}/drive/static/${payment.document_file.settings.final_name}`;
        window.open(url, "_blank");
        return;
      } else {
        let mimetype = payment.document_file.settings.mimetype;
        let download_extension = "";
        if (mimetype == "application/pdf") download_extension = "pdf";
        else if (mimetype == "image/jpeg") download_extension = "jpg";
        else if (mimetype == "image/png") download_extension = "png";
        else if (mimetype == "text/plain") download_extension = "txt";
        else if (
          mimetype ==
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          download_extension = "docx";
        else if (
          mimetype ==
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
          download_extension = "xlsx";
        else if (
          mimetype ==
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        )
          download_extension = "pptx";
        else if (mimetype == "application/vnd.ms-excel")
          download_extension = "xls";
        else if (mimetype == "application/msword") download_extension = "doc";

        let url = `${api_host}/drive/static/${payment.document_file.settings.final_name}`;
        let download_name = `payment_receipt_order_${payment.order_id}.${download_extension}`;
        fetch(url)
          .then((response) => {
            return response.blob();
          })
          .then((blob) => {
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = url;
            a.download = download_name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
          })
          .catch((error) => {
            console.error(error);
          });
      }
      // let url = `${api_host}/tailoring_order/payment_document/${payment.uuid}`;
      // download(url, "payment_document");
      // let final_name = payment.document_file.name;

      // let url = `${api_host}/drive/static/${payment.document_file.settings.final_name}`;
      // window.open(url, "_blank");

      // download(url, final_name)
      //   .then(() => {
      //     console.log("downloaded");
      //   })
      //   .catch((error) => {
      //     console.log(error);
      //   });
    },
  },
  mounted() {},
  watch: {},
};
</script>
<style>
.box {
  border-radius: 14px;
  box-shadow: 0 2px 12px rgba(44, 62, 80, 0.08);
}
.table th,
.table td {
  vertical-align: middle !important;
  font-size: 0.97em;
}
.tag {
  margin-bottom: 2px;
}
</style>
